import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAcademicYear } from '../contexts/AcademicYearContext';
import { FiPrinter, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import Swal from 'sweetalert2';
import ListSpinner from '../components/common/ListSpinner';
import MobileCard from '../components/common/MobileCard';
import MobileCardList from '../components/common/MobileCardList';

const LOADING_HTML = `
<html>
  <head><title>Chargement du relevé...</title></head>
  <body style="margin:0;padding:0;font-family:'Segoe UI',Arial,sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;background:linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
    <div style="text-align:center;background:white;padding:50px 40px;border-radius:20px;box-shadow:0 20px 60px rgba(0,0,0,0.3);max-width:400px;width:90%">
      <div style="position:relative;width:80px;height:80px;margin:0 auto 30px">
        <div style="position:absolute;top:0;left:0;width:100%;height:100%;border:4px solid rgba(102,126,234,0.2);border-top:4px solid #667eea;border-radius:50%;animation:spin 1s linear infinite"></div>
        <div style="position:absolute;top:10px;left:10px;width:60px;height:60px;border:4px solid rgba(118,75,162,0.2);border-top:4px solid #764ba2;border-radius:50%;animation:spin 1s reverse linear infinite"></div>
        <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);font-size:32px">📄</div>
      </div>
      <h2 style="margin:0 0 10px 0;font-size:24px;color:#333;font-weight:600">Génération du relevé</h2>
      <p style="margin:0 0 30px 0;font-size:14px;color:#666">Votre document est en cours de préparation...</p>
      <div style="background:#f3f4f6;height:4px;border-radius:2px;overflow:hidden;margin:0 auto">
        <div style="background:linear-gradient(90deg, #667eea 0%, #764ba2 100%);height:100%;width:30%;border-radius:2px;animation:progress 2s ease-in-out infinite"></div>
      </div>
    </div>
    <style>
      @keyframes spin { 
        0% { transform: rotate(0deg); } 
        100% { transform: rotate(360deg); } 
      }
      @keyframes progress { 
        0% { transform: translateX(-100%); } 
        50% { transform: translateX(400%); } 
        100% { transform: translateX(1000%); } 
      }
    </style>
  </body>
</html>
`;

const RelevesNotesPage = () => {
  const { selectedYear } = useAcademicYear();
  const [ouvertures, setOuvertures] = useState([]);
  const [relevesData, setRelevesData] = useState([]);
  const [filteredRelevesData, setFilteredRelevesData] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [selectedClasse, setSelectedClasse] = useState('');
  const [selectedMois, setSelectedMois] = useState('');

  useEffect(() => {
    if (selectedYear) {
      fetchOuvertures(selectedYear.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  useEffect(() => {
    if (ouvertures.length > 0) {
      fetchRelevesData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvertures]);

  const fetchOuvertures = async (anneeId) => {
    try {
      const res = await api.get('/ouvertures-classes');
      const filtered = (res.data.data || []).filter(o => o.annee_scolaire_id === anneeId);
      setOuvertures(filtered);
    } catch (error) {
      console.error('Erreur lors du chargement des ouvertures:', error);
    }
  };

  const fetchRelevesData = async () => {
    setLoading(true);
    try {
      // Récupérer tous les mois uniques des évaluations
      const evaluationsRes = await api.get('/evaluations');
      const evaluations = evaluationsRes.data.data || [];
      
      // Extraire les mois uniques des dates d'évaluation
      const moisUniques = [...new Set(
        evaluations
          .filter(evaluation => {
            // Filtrer par les ouvertures de classe de l'année courante
            return ouvertures.some(oc => oc.ouverture_classe_id === evaluation.ouverture_classe_id);
          })
          .map(evaluation => {
            if (!evaluation.date_evaluation) return null;
            const date = new Date(evaluation.date_evaluation);
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          })
          .filter(m => m !== null)
      )].sort();

      // Pour chaque ouverture de classe et chaque mois, créer une entrée
      const releves = [];
      ouvertures.forEach(ouverture => {
        moisUniques.forEach(mois => {
          // Extraire correctement le nom de la classe
          let classeNom = 'N/A';
          if (typeof ouverture.classe === 'string') {
            classeNom = ouverture.classe;
          } else if (typeof ouverture.classe === 'object' && ouverture.classe?.classe) {
            classeNom = ouverture.classe.classe;
          }
          
          let classeAbrev = '';
          if (typeof ouverture.abrev === 'string') {
            classeAbrev = ouverture.abrev;
          } else if (typeof ouverture.classe === 'object' && ouverture.classe?.abrev) {
            classeAbrev = ouverture.classe.abrev;
          }
          
          releves.push({
            ouverture_classe_id: ouverture.ouverture_classe_id || ouverture.id,
            classe: classeNom,
            classe_abrev: classeAbrev,
            mois: mois,
            mois_display: formatMonth(mois)
          });
        });
      });

      setRelevesData(releves);
      setFilteredRelevesData(releves);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les données selon les sélections
  useEffect(() => {
    let filtered = [...relevesData];
    
    if (selectedClasse) {
      filtered = filtered.filter(releve => {
        const classeMatch = releve.classe === selectedClasse || 
                           releve.classe_abrev === selectedClasse;
        return classeMatch;
      });
    }
    
    if (selectedMois) {
      filtered = filtered.filter(releve => releve.mois === selectedMois);
    }
    
    setFilteredRelevesData(filtered);
  }, [selectedClasse, selectedMois, relevesData]);

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const months = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  const toggleRow = (key) => {
    setExpandedRows(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handlePrintClasse = async (releve) => {
    // Ouvrir l'onglet immédiatement pour éviter le blocage des pop-ups
    const windowRef = window.open('', '_blank');
    
    // Afficher un message de chargement dans l'onglet
    if (windowRef) {
      windowRef.document.write(LOADING_HTML);
    }
    
    try {
      setPrinting(true);
      
      // Attendre que le PDF soit complètement chargé
      const response = await api.get(
        `/releves-notes/classe/${releve.ouverture_classe_id}/${releve.mois}`,
        { responseType: 'blob' }
      );
      
      setPrinting(false);
      
      // Charger le PDF dans l'onglet déjà ouvert
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      windowRef.location.href = url;
    } catch (error) {
      setPrinting(false);
      if (windowRef && !windowRef.closed) {
        windowRef.close();
      }
      
      let errorMessage = 'Erreur lors de l\'impression du relevé.';
      
      // Gérer les erreurs - l'intercepteur axios devrait déjà avoir converti les blobs
      if (error.response?.data) {
        if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      console.error('Erreur lors de l\'impression:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur de génération',
        text: errorMessage,
      });
    }
  };

  const handlePrintIndividuel = async (inscriptionId, releve) => {
    // Ouvrir l'onglet immédiatement pour éviter le blocage des pop-ups
    const windowRef = window.open('', '_blank');
    
    // Afficher un message de chargement dans l'onglet
    if (windowRef) {
      windowRef.document.write(LOADING_HTML);
    }
    
    try {
      setPrinting(true);
      
      // Attendre que le PDF soit complètement chargé
      const response = await api.get(
        `/releves-notes/eleve/${inscriptionId}/${releve.mois}`,
        { responseType: 'blob' }
      );
      
      setPrinting(false);
      
      // Charger le PDF dans l'onglet déjà ouvert
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      windowRef.location.href = url;
    } catch (error) {
      setPrinting(false);
      if (windowRef && !windowRef.closed) {
        windowRef.close();
      }
      
      let errorMessage = 'Erreur lors de l\'impression du relevé individuel.';
      
      // Gérer les erreurs - l'intercepteur axios devrait déjà avoir converti les blobs
      if (error.response?.data) {
        if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      console.error('Erreur lors de l\'impression:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur de génération',
        text: errorMessage,
      });
    }
  };

  return (
    <div>
      {/* Spinner plein écran pendant le chargement du PDF */}
      {printing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 shadow-2xl">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-600 border-t-transparent mx-auto mb-4"></div>
              <p className="text-lg font-semibold text-gray-800">Génération du relevé en cours...</p>
              <p className="text-sm text-gray-500 mt-2">Veuillez patienter</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="page-header">
        <h1 className="page-title">Relevés de notes mensuels</h1>
      </div>

      {/* Filtres */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Filtre par classe */}
          <div>
            <label htmlFor="filter-classe" className="block text-sm font-medium text-gray-700 mb-2">
              Filtrer par classe
            </label>
            <select
              id="filter-classe"
              value={selectedClasse}
              onChange={(e) => setSelectedClasse(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Toutes les classes</option>
              {[...new Set(relevesData.map(r => r.classe))].sort().map(classe => (
                <option key={classe} value={classe}>{classe}</option>
              ))}
            </select>
          </div>

          {/* Filtre par mois */}
          <div>
            <label htmlFor="filter-mois" className="block text-sm font-medium text-gray-700 mb-2">
              Filtrer par mois
            </label>
            <select
              id="filter-mois"
              value={selectedMois}
              onChange={(e) => setSelectedMois(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Tous les mois</option>
              {[...new Set(relevesData.map(r => r.mois))].sort().map(mois => (
                <option key={mois} value={mois}>{formatMonth(mois)}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Bouton pour réinitialiser les filtres */}
        {(selectedClasse || selectedMois) && (
          <div className="mt-4">
            <button
              onClick={() => {
                setSelectedClasse('');
                setSelectedMois('');
              }}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition"
            >
              Réinitialiser les filtres
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <ListSpinner />
      ) : (
        <>
          <MobileCardList
            className="md:hidden"
            isEmpty={filteredRelevesData.length === 0}
            emptyMessage={relevesData.length === 0 ? 'Aucune donnée disponible' : 'Aucun résultat ne correspond aux filtres'}
          >
            {filteredRelevesData.map((releve) => {
              const uniqueKey = `${releve.ouverture_classe_id}-${releve.mois}`;
              return (
                <div key={uniqueKey}>
                  <MobileCard
                    title={releve.classe}
                    subtitle={releve.mois_display}
                    actions={
                      <>
                        <button
                          onClick={() => handlePrintClasse(releve)}
                          className="inline-flex items-center px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-xs"
                        >
                          <FiPrinter className="mr-1" size={14} /> Imprimer
                        </button>
                        <button
                          onClick={() => toggleRow(uniqueKey)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                        >
                          {expandedRows[uniqueKey] ? <FiChevronUp /> : <FiChevronDown />}
                        </button>
                      </>
                    }
                  />
                  {expandedRows[uniqueKey] && (
                    <ReleveDetailCards
                      ouvertureClasseId={releve.ouverture_classe_id}
                      mois={releve.mois}
                      onPrintIndividuel={handlePrintIndividuel}
                    />
                  )}
                </div>
              );
            })}
          </MobileCardList>

          <div className="hidden md:block bg-white shadow rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Classe
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mois
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Imprimer le relevé
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  Détail
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRelevesData.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                    {relevesData.length === 0 
                      ? 'Aucune donnée disponible' 
                      : 'Aucun résultat ne correspond aux filtres sélectionnés'}
                  </td>
                </tr>
              ) : (
                filteredRelevesData.map((releve, index) => {
                  const uniqueKey = `${releve.ouverture_classe_id}-${releve.mois}`;
                  return (
                    <React.Fragment key={uniqueKey}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {releve.classe}
                          {releve.classe_abrev && <span className="text-gray-500 ml-2">({releve.classe_abrev})</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {releve.mois_display}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <button
                            onClick={() => handlePrintClasse(releve)}
                            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                          >
                            <FiPrinter className="mr-2" />
                            Imprimer
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <button
                            onClick={() => toggleRow(uniqueKey)}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            {expandedRows[uniqueKey] ? <FiChevronUp /> : <FiChevronDown />}
                          </button>
                        </td>
                      </tr>
                      {expandedRows[uniqueKey] && (
                        <ReleveDetailRow 
                          ouvertureClasseId={releve.ouverture_classe_id} 
                          mois={releve.mois}
                          onPrintIndividuel={handlePrintIndividuel}
                        />
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
          </div>
        </>
      )}
    </div>
  );
};

const ReleveDetailCards = ({ ouvertureClasseId, mois, onPrintIndividuel }) => {
  const [inscriptions, setInscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/inscriptions', { params: { ouverture_classe_id: ouvertureClasseId } })
      .then((res) => setInscriptions(res.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [ouvertureClasseId]);

  if (loading) return <ListSpinner className="py-4" />;
  if (inscriptions.length === 0) {
    return <div className="mobile-card-empty text-xs py-3 mt-2">Aucun élève inscrit</div>;
  }

  return (
    <div className="mt-2 ml-1 space-y-2">
      {inscriptions.map((insc) => (
        <MobileCard
          key={insc.id}
          title={`${insc.prenom || ''} ${insc.nom || ''}`.trim() || 'Élève'}
          subtitle={insc.matricule || 'N/A'}
          actions={
            <button
              onClick={() => onPrintIndividuel(insc.id, { mois })}
              className="inline-flex items-center px-2 py-1 bg-primary-600 text-white rounded text-xs"
            >
              <FiPrinter className="mr-1" size={12} /> Imprimer
            </button>
          }
        />
      ))}
    </div>
  );
};

const ReleveDetailRow = ({ ouvertureClasseId, mois, onPrintIndividuel }) => {
  const [inscriptions, setInscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvertureClasseId]);

  const fetchInscriptions = async () => {
    try {
      const res = await api.get('/inscriptions', {
        params: { ouverture_classe_id: ouvertureClasseId }
      });
      setInscriptions(res.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des inscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <tr>
        <td colSpan="4" className="px-6 py-4 text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
        </td>
      </tr>
    );
  }

  return (
    <>
      <tr className="bg-gray-100 border-t-2">
        <td colSpan="4" className="px-6 py-3">
          <div className="bg-white rounded-lg p-4">
            <table className="min-w-full">
              <thead>
                <tr className="border-b">
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Inscription ID</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Matricule</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Nom</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Prénom</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-600 uppercase">Action</th>
                </tr>
              </thead>
              <tbody>
                {inscriptions.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-2 text-center text-gray-500">Aucun élève inscrit</td>
                  </tr>
                ) : (
                  inscriptions.map((insc) => (
                    <tr key={insc.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-900">{insc.id}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">{insc.matricule || 'N/A'}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">{insc.nom || 'N/A'}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">{insc.prenom || 'N/A'}</td>
                      <td className="px-4 py-2 text-center">
                        <button
                          onClick={() => onPrintIndividuel(insc.id, { mois })}
                          className="inline-flex items-center px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 transition text-sm"
                        >
                          <FiPrinter className="mr-1" />
                          Print individuel
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </td>
      </tr>
    </>
  );
};

export default RelevesNotesPage;
